import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replohq/lattice/components/table/Table";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { formatPrice } from "replo-utils/lib/format";

import {
  ALWAYS_FRESH,
  eventTypeTagVariant,
  formatDate,
  StripeLink,
} from "./shared";

export const SubscriptionChangeLogsTable: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const { data, isLoading } = agentBillingRouter.changelogEntries.find.useQuery(
    workspaceId ? { workspaceId, limit: 50 } : skipToken,
    ALWAYS_FRESH,
  );

  if (isLoading) {
    return <div className="text-muted py-3 text-xs">Loading...</div>;
  }

  const logs = data?.changeLogs ?? [];
  if (logs.length === 0) {
    return <div className="text-muted py-3 text-xs">No change logs found.</div>;
  }

  return (
    <div className="max-h-[350px] overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Prev $/mo</TableHead>
            <TableHead>Curr $/mo</TableHead>
            <TableHead>Stripe Event</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const isExpanded = expandedId === log.id;

            return (
              <React.Fragment key={log.id}>
                <TableRow
                  onClick={() => setExpandedId(isExpanded ? null : log.id)}
                >
                  <TableCell>
                    <span className="whitespace-nowrap">
                      {formatDate(log.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Tag variant={eventTypeTagVariant(log.eventType)} size="sm">
                      {log.eventType}
                    </Tag>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {log.previousMonthlyAmount != null && log.previousCurrency
                        ? formatPrice({
                            amount: Number(log.previousMonthlyAmount),
                            currency: log.previousCurrency,
                          })
                        : "--"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {formatPrice({
                        amount: Number(log.currentMonthlyAmount),
                        currency: log.currentCurrency,
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StripeLink value={log.stripeEventId} />
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={5} UNSAFE_className="bg-sheet p-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <div className="text-muted mb-2 text-xs font-medium">
                            Previous Snapshot
                          </div>
                          <pre className="border-border max-h-[200px] overflow-auto rounded-lg border bg-white p-3 font-mono text-xs">
                            {log.previousSnapshot
                              ? JSON.stringify(log.previousSnapshot, null, 2)
                              : "null"}
                          </pre>
                        </div>
                        <div className="flex-1">
                          <div className="text-muted mb-2 text-xs font-medium">
                            Current Snapshot
                          </div>
                          <pre className="border-border max-h-[200px] overflow-auto rounded-lg border bg-white p-3 font-mono text-xs">
                            {log.currentSnapshot
                              ? JSON.stringify(log.currentSnapshot, null, 2)
                              : "null"}
                          </pre>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
